import { Component, input, linkedSignal, OnInit, output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerInputEvent,
} from '@angular/material/datepicker';
import { MatInput } from '@angular/material/input';
import moment from 'moment';

@Component({
  selector: 'table-datatable-date-with-relative-weeks',
  templateUrl: './datatable-date-with-relative-weeks.component.html',
  styleUrls: ['./datatable-date-with-relative-weeks.component.scss'],
  imports: [MatDatepicker, MatInput, MatDatepickerInput, ReactiveFormsModule],
})
export class DatatableDateWithRelativeWeeksComponent implements OnInit {
  date = input.required<moment.Moment>();
  dateOutput = output<moment.Moment>();
  baseDate = input.required<moment.Moment>();
  label = input.required<string>();
  hideRelativeWeeksAndDays = input<boolean>(false);
  hideRelativeDays = input<boolean>(false);
  disabled = input<boolean>(false);

  _date = signal<moment.Moment>(undefined);
  relativeWeeks = signal<number>(undefined);
  relativeDays = signal<number>(undefined);
  relativeWeeksDisplay = signal<number>(undefined);

  dateFormControl = linkedSignal<boolean, FormControl>({
    source: this.disabled,
    computation: (source, previous) => {
      if (!previous) {
        return new FormControl({ value: undefined, disabled: source });
      }
      return new FormControl({ value: previous.value.value, disabled: source });
    },
  });

  ngOnInit() {
    this.setDate(this.date(), false);
    this.updateRelativeDataBasedOnDate();
  }

  getBaseDate() {
    return this.utcDay(this.baseDate());
  }

  setDate(newDate: moment.Moment | undefined, emit = true) {
    const date = this.utcDay(newDate);
    this._date.set(date);
    this.dateFormControl().setValue(
      date ? new Date(date.year(), date.month(), date.date()) : undefined,
    );
    if (emit) {
      this.dateOutput.emit(date);
    }
  }

  /**
   * Due dates are calendar days stored as UTC-midnight instants. Rounding with the local wall clock
   * shifts the day for negative UTC offsets, so always take the UTC calendar day.
   */
  private utcDay(value: moment.MomentInput | undefined): moment.Moment | undefined {
    return value ? moment.utc(value).startOf('day') : undefined;
  }

  private localDayToUtc(value: moment.MomentInput | undefined): moment.Moment | undefined {
    if (!value) {
      return undefined;
    }
    const local = moment(value);
    return moment.utc([local.year(), local.month(), local.date()]);
  }

  updateRelativeDataBasedOnDate() {
    const relativeWeeks = this._date().diff(this.getBaseDate(), 'w', false);
    if (this.relativeWeeks() != relativeWeeks) {
      this.relativeWeeks.set(relativeWeeks);
    }
    const relativeDays = this._date()
      .clone()
      .subtract(relativeWeeks, 'w')
      .diff(this.getBaseDate(), 'd', false);
    if (this.relativeDays() != relativeDays) {
      this.relativeDays.set(relativeDays);
    }
    this.relativeWeeksDisplay.set(relativeWeeks + (relativeDays > 3 ? 1 : 0));
  }

  updateDateBasedOnRelativeData() {
    const newDate = this.getBaseDate().add(this.relativeWeeks(), 'w').add(this.relativeDays(), 'd');
    this.setDate(newDate);
  }

  dateChangedByUser(event: MatDatepickerInputEvent<Date>) {
    this.setDate(this.localDayToUtc(event.value));
    this.updateRelativeDataBasedOnDate();
  }

  relativeWeeksBlurred(relativeWeeks: string) {
    const newRelativeWeeks = Number(relativeWeeks ?? 0);
    if (this.relativeWeeks() === newRelativeWeeks) {
      return;
    }
    this.relativeWeeks.set(newRelativeWeeks);
    if (this.hideRelativeDays()) {
      this.relativeDays.set(0);
    }
    this.updateDateBasedOnRelativeData();
  }

  relativeDaysBlurred(relativeDays: string) {
    const newRelativeDays = Number(relativeDays ?? 0);
    if (newRelativeDays === this.relativeDays()) {
      return;
    }
    this.relativeDays.set(newRelativeDays);
    this.updateDateBasedOnRelativeData();
  }
}
